import { FileImageOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  Flex,
  Grid,
  Popconfirm,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Dispatch, Fragment, SetStateAction, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getOvertimeTicket, confirmOvertimeTicket } from 'src/apis/hr_ticket/overtimeTicket.api';

import { CheckIcon, NotCheckIcon } from 'src/assets/icons';
import { BtnCustom } from 'src/components/v2/Button';
import Icon from 'src/components/v2/Icon';
import urlPath from 'src/constants/urlPath';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import OvertimeTicketAttach from 'src/pages/v2/HrTicket/OvertimeTicketDetail/OvertimeTicketAttach';
import { AttachOpenType } from 'src/pages/v2/HrTicket/OvertimeTicketDetail/OvertimeTicketDetail';
import { TicketStatus } from 'src/types/helpdesk/ticket.type';
import { IBiometricDataLog } from 'src/types/hr_ticket/biometricDataLog.type';
import {
  ConfirmOvertimeRq,
  IOvertimeAttachFile,
  IOvertimeTicket,
  OvertimeDetailConfirmed,
  OvertimeTicketProgress
} from 'src/types/hr_ticket/overtimeTicket.type';
import { IEmployeeShort } from 'src/types/master_data/employee.type';
import { showNotification } from 'src/utils/notification';
import { OpenDrawerType } from '../../TicketRelative/OvertimeTicket';
import '../TicketRelativeDrawer.scss';
import AddNoteForUnConfirm from './AddNoteForUnConfirm';
import { convertMinutesToHours, formatDate, formatTime, formatTimeString } from 'src/utils/utils';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { TicketDetailContext } from 'src/contexts/ticketDetail.context';
import { AppContext } from 'src/contexts/app.context';

const { Text, Title } = Typography;

type Props = {
  open: OpenDrawerType;
  setOpen: Dispatch<SetStateAction<OpenDrawerType>>;
};

export type OpenAddNoteType = {
  id?: number;
  code?: string;
  isOpen: boolean;
};

const columns = function (isSupervisorConfirmed?: ConfirmTypeEn): ColumnsType<OvertimeDetailConfirmed> {
  return [
    {
      title: 'Người thực hiện',
      dataIndex: 'employee',
      key: 'employee',
      width: '20%',
      render: (value: IEmployeeShort) => <Text strong>{value.fullName}</Text>
    },
    {
      title: 'TG. Tăng ca',
      dataIndex: '',
      key: 'time',
      width: '5%',
      align: 'left',
      render: (_, record) => (
        <Flex vertical gap={5}>
          <Descriptions
            className='overtime-ticket-time'
            layout='horizontal'
            style={{ width: '270px' }}
            labelStyle={{ width: '85px' }}
            column={1}
          >
            <Descriptions.Item label='Đăng ký'>
              <Flex gap={5} justify='center'>
                <Text>{formatTimeString(record.startTime, true) ?? ''}</Text>-
                <Text>{formatTimeString(record.endTime) ?? ''}</Text>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label='Chấm công'>
              <Flex gap={5} justify='center'>
                <Text>{formatTimeString(record.checkInTime, true) ?? ''}</Text>-
                <Text>{formatTimeString(record.checkOutTime) ?? ''}</Text>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label='Xác nhận'>
              {isSupervisorConfirmed === ConfirmTypeEn.WaitingConfirm ? (
                <Text type='secondary'>Chưa xác nhận</Text>
              ) : !record.isSupervisorUnconfirmed ? (
                <Flex gap={5} justify='center'>
                  <Text strong type='success'>
                    {formatTimeString(record.actualStartTime, true) ?? ''}
                  </Text>
                  -
                  <Text strong type='success'>
                    {formatTimeString(record.actualEndTime) ?? ''}
                  </Text>
                  <Text type='success'>({convertMinutesToHours(record.actualTotalTime)})</Text>
                </Flex>
              ) : (
                <Text type='danger' strong>
                  Không xác nhận
                </Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label='Ngày'>
              <Text strong>{formatDate(record.date)}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Flex>
      )
    },
    {
      title: <Text>Làm tại công trình</Text>,
      dataIndex: '',
      key: 'isWorkAtConstructionSite',
      width: '6%',
      align: 'center',
      render: (_, record) => {
        return (
          <Flex justify='center'>
            <Icon
              url={record.isWorkAtConstructionSite ? CheckIcon : NotCheckIcon}
              size={20}
              titleTooltip={record.isWorkAtConstructionSite ? 'Có' : 'Không'}
            />
          </Flex>
        );
      }
    },
    {
      title: 'Ghi chú công việc',
      dataIndex: '',
      key: 'note',
      width: '20%',
      align: 'left',
      render: (record: OvertimeDetailConfirmed) => {
        return <Text>{record.supervisorConfirmedNote}</Text>;
      }
    }
  ];
};

const progressColumns = function (
  setOpenAttach: Dispatch<SetStateAction<AttachOpenType>>
): ColumnsType<OvertimeTicketProgress> {
  return [
    {
      title: 'Tên công việc',
      dataIndex: '',
      key: 'jobTitle',
      width: '15%',
      render: (record) => {
        return <Text>{record?.manpowerSupplyFormDetail?.jobTitle}</Text>;
      }
    },
    {
      title: 'Chi tiết công việc',
      dataIndex: '',
      key: 'jobDetail',
      width: '20%',
      align: 'left',
      render: (record) => <Text>{record?.manpowerSupplyFormDetail?.jobDetail}</Text>
    },
    {
      title: 'Yêu cầu đặc thù',
      dataIndex: '',
      key: 'jobRequirement',
      width: '20%',
      align: 'left',
      render: (record) => <Text>{record?.manpowerSupplyFormDetail?.jobRequirement}</Text>
    },

    {
      title: 'Tiến độ (%)',
      dataIndex: 'percentageCompletion',
      key: 'percentageCompletion',
      align: 'left',
      render: (_, record) => {
        return (
          <Flex vertical>
            <Text>{record.percentageCompletion}%</Text>
            {record.isCompleted ? (
              <Text type='success' strong>
                Đã xác nhận
              </Text>
            ) : (
              <Text type='danger' strong>
                Không xác nhận
              </Text>
            )}
          </Flex>
        );
      }
    },
    {
      title: 'Ghi chú',
      dataIndex: 'note',
      key: 'note',
      width: '15%',
      align: 'left',
      render: (_, record) => {
        return <Text>{record.note}</Text>;
      }
    },
    {
      title: 'Action',
      dataIndex: '',
      key: 'note',
      width: '15%',
      align: 'left',
      render: (_, record) => {
        return (
          <Button
            size='small'
            icon={<FileImageOutlined />}
            onClick={() =>
              setOpenAttach((prev) => ({
                ...prev,
                isOpen: true,
                attachList: record.overtimeTicketProgressAttach as IOvertimeAttachFile[]
              }))
            }
          >
            Kết quả CV
          </Button>
        );
      }
    }
  ];
};

export const TicketStatusTagV2 = (overtimeTicket: IOvertimeTicket | undefined) => {
  if (overtimeTicket?.ticketStatus?.id === TicketStatus.New) {
    return <Tag color={'var(--color-new)'}>{overtimeTicket?.ticketStatus?.name}</Tag>;
  }

  if (overtimeTicket?.ticketStatus?.id === TicketStatus.Reject) {
    return <Tag color={'var(--color-rejected)'}>{overtimeTicket?.ticketStatus?.name}</Tag>;
  }

  if (overtimeTicket?.ticketStatus?.id === TicketStatus.Canceled) {
    return <Tag color={'var(--color-canceled)'}>{overtimeTicket?.ticketStatus?.name}</Tag>;
  }

  if (
    overtimeTicket?.ticketStatus?.id === TicketStatus.ApprovedRequest &&
    overtimeTicket?.isConfirm === ConfirmTypeEn.Confirmed
  ) {
    return <Tag color={'var(--color-approved-request)'}>Hoàn thành</Tag>;
  }

  if (
    overtimeTicket?.ticketStatus?.id === TicketStatus.ApprovedRequest &&
    overtimeTicket?.isConfirm === ConfirmTypeEn.Reject
  ) {
    return <Tag color={'var(--color-approved-request)'}>Đã không xác nhận - Raise ticket</Tag>;
  }

  if (
    overtimeTicket?.ticketStatus?.id === TicketStatus.ApprovedRequest &&
    (overtimeTicket?.isSupervisorConfirmed === undefined || overtimeTicket?.isSupervisorConfirmed === null)
  ) {
    return <Tag color={'var(--color-approved-request)'}>Chờ xác nhận - GS</Tag>;
  }

  if (overtimeTicket?.ticketStatus?.id === TicketStatus.ApprovedRequest && overtimeTicket?.isSupervisorConfirmed) {
    return <Tag color={'var(--color-approved-request)'}>Chờ xác nhận - Raise ticket</Tag>;
  }

  if (
    overtimeTicket?.ticketStatus?.id === TicketStatus.ApprovedRequest &&
    overtimeTicket?.isSupervisorConfirmed === ConfirmTypeEn.Reject
  ) {
    return <Tag color={'var(--color-approved-request)'}>Đã không xác nhận - GS</Tag>;
  }

  return <Tag>{overtimeTicket?.ticketStatus.name}</Tag>;
};

function OvertimeTicketDrawer({ open, setOpen }: Props) {
  const [overtimeConfirms, setOvertimeConfirms] = useState<OvertimeDetailConfirmed[]>([]);
  const [openAddNote, setOpenAddNote] = useState<OpenAddNoteType>({
    isOpen: false
  });
  const [openAttach, setOpenAttach] = useState<AttachOpenType>({ isOpen: false, attachList: [] });
  const queryClient = useQueryClient();
  const { ticket } = useContext(TicketDetailContext);
  const { userInfo } = useContext(AppContext);

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const overtimeTicketQuery = useQuery({
    queryKey: ['overtimeTicket', open.id],
    queryFn: () => getOvertimeTicket(Number(open.id)),
    enabled: !!open.id
  });

  const confirmOvertimeTicketMutation = useMutation({
    mutationFn: (body: ConfirmOvertimeRq) => confirmOvertimeTicket(body),
    onSuccess: (_, body) => {
      showNotification('Xác nhận phiếu tăng ca thành công', 'success');
      queryClient.invalidateQueries(['refOvertimeTicketsForTicket', overtimeTicketQuery.data?.data.refTicketId]);
      handleRefresh(body.id);
    }
  });

  useEffect(() => {
    if (overtimeTicketQuery.data) {
      const confirms = overtimeTicketQuery.data?.data?.overtimeTicketDetails?.map((item) => {
        const biometricTime: IBiometricDataLog[] | undefined = overtimeTicketQuery.data?.data?.finalConfirms
          ?.find((finalItem) => finalItem.id === item.id)
          ?.biometricDataLogs?.sort((a, b) => new Date(a.record).getTime() - new Date(b.record).getTime());
        const confirmedItem = overtimeTicketQuery.data?.data?.overtimeTicketDetails?.find(
          (confirmedItem) => confirmedItem.employeeId === item.employeeId && confirmedItem.date === item.date
        );

        return {
          ...item,
          startBiometricTime: formatTime(biometricTime?.[0].record),
          endBiometricTime: formatTime(biometricTime?.[biometricTime?.length - 1].record),
          checkInTime: item.checkInTime,
          checkOutTime: item.checkOutTime,
          actualStartTime: overtimeTicketQuery.data.data.isSupervisorConfirmed
            ? confirmedItem?.actualStartTime
            : confirmedItem?.startTime,
          actualEndTime: overtimeTicketQuery.data.data.isSupervisorConfirmed
            ? confirmedItem?.actualEndTime
            : confirmedItem?.endTime,
          supervisorConfirmedNote: overtimeTicketQuery.data.data.isSupervisorConfirmed
            ? confirmedItem?.supervisorConfirmedNote
            : '',
          isNew: false
        };
      });

      setOvertimeConfirms(confirms as OvertimeDetailConfirmed[]);
    }
  }, [overtimeTicketQuery.data]);

  const handleConfirmOvertime = (rq: ConfirmOvertimeRq) => {
    confirmOvertimeTicketMutation.mutate({
      id: rq.id,
      isConfirm: rq.isConfirm
    });
  };

  const handleRefresh = (id: number) => {
    queryClient.invalidateQueries(['overtimeTicket', id]);
    queryClient.invalidateQueries(['refOvertimeTicketsForTicket', overtimeTicketQuery.data?.data.refTicketId]);
  };

  return (
    <Drawer
      title={
        <Flex justify='space-between'>
          <Text>Thông tin tăng ca</Text>
          {ticket?.data.createdByObj.id === userInfo?.id &&
            ticket?.data.ticketStatusId !== TicketStatus.New &&
            overtimeTicketQuery.data?.data.isSupervisorConfirmed === ConfirmTypeEn.Confirmed &&
            overtimeTicketQuery.data?.data.isConfirm === ConfirmTypeEn.WaitingConfirm && (
              <Space>
                <Popconfirm
                  title={'Bạn có đồng ý với kết quả công việc đã thực hiện?'}
                  onConfirm={() =>
                    handleConfirmOvertime({
                      id: overtimeTicketQuery.data?.data?.id as number,
                      isConfirm: ConfirmTypeEn.Confirmed
                    })
                  }
                >
                  <BtnCustom type='confirm' size='small' isLoading={confirmOvertimeTicketMutation.isLoading} />
                </Popconfirm>
                <BtnCustom
                  type='noConfirm'
                  size='small'
                  onClick={() =>
                    setOpenAddNote({
                      isOpen: true,
                      id: overtimeTicketQuery.data?.data.id,
                      code: overtimeTicketQuery.data?.data.code
                    })
                  }
                />
              </Space>
            )}
        </Flex>
      }
      rootClassName='overtime-ticket-drawer'
      styles={{ header: { borderBlockEnd: '1px solid #f0f0f0' } }}
      open={open.isOpen}
      onClose={() =>
        setOpen({
          isOpen: false
        })
      }
      width={'50%'}
      mask={true}
    >
      <Fragment>
        {overtimeTicketQuery.isFetching ? (
          <Skeleton />
        ) : (
          <Fragment>
            <Descriptions
              className='overtime-ticket-descriptions'
              layout='horizontal'
              labelStyle={{ width: '135px' }}
              column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 2, xxl: 2 }}
            >
              <Descriptions.Item label='Mã phiếu'>
                <Text strong>{overtimeTicketQuery.data?.data?.code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Trạng thái'>
                <HRTicketStatusTag
                  ticketStatus={overtimeTicketQuery.data?.data.ticketStatus}
                  erpKey={overtimeTicketQuery.data?.data.erpKey}
                  isSupervisorConfirmed={overtimeTicketQuery.data?.data.isSupervisorConfirmed}
                  isConfirm={overtimeTicketQuery.data?.data.isConfirm}
                  hrConfirmedType={overtimeTicketQuery.data?.data.hrConfirmedType}
                />
              </Descriptions.Item>
              <Descriptions.Item label='Ticket raise' span={colspan}>
                <Text strong>
                  <Link to={`${urlPath.myTicket.location}/${overtimeTicketQuery.data?.data?.refTicketId}`}>
                    {overtimeTicketQuery.data?.data?.refTicketCode}
                  </Link>
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label='Người giám sát' span={colspan}>
                <Text strong>{overtimeTicketQuery.data?.data?.supervisor?.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Người tạo'>
                <Text>{overtimeTicketQuery.data?.data?.createdBy?.fullName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Loại tăng ca'>
                <Text>{overtimeTicketQuery.data?.data?.overtimeType?.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Ngày tạo' span={colspan}>
                <Text strong>{formatDate(overtimeTicketQuery.data?.data?.createdDate)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Bộ phận' span={colspan}>
                <Text>{overtimeTicketQuery.data?.data?.createdBy?.department?.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Số phiếu ERP' span={colspan}>
                {overtimeTicketQuery.data?.data?.erpKey}
              </Descriptions.Item>
              <Descriptions.Item label={'Lý do tăng ca'} span={colspan}>
                <Text>{overtimeTicketQuery.data?.data?.note || ''}</Text>
              </Descriptions.Item>
            </Descriptions>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <Flex vertical gap={10}>
              {overtimeTicketQuery.data?.data?.isSupervisorConfirmed === ConfirmTypeEn.Confirmed && (
                <Fragment>
                  <Title level={5} style={{ marginBottom: '0px' }}>
                    Kết quả công việc
                  </Title>
                  <Table
                    className='overtime-progress-confirm-table'
                    columns={progressColumns(setOpenAttach)}
                    rowKey={(record) => String(record.manpowerSupplyFormDetailId)}
                    dataSource={overtimeTicketQuery.data?.data?.overtimeTicketProgresses}
                    pagination={false}
                    scroll={{ x: 400 }}
                    bordered={false}
                    size='middle'
                  />
                </Fragment>
              )}
              <Fragment>
                <Title level={5} style={{ marginBottom: '0px' }}>
                  Danh sách tăng ca
                </Title>
                <Table
                  className='overtime-confirm-table'
                  columns={columns(overtimeTicketQuery.data?.data.isSupervisorConfirmed)}
                  rowKey={(record) => String(record.id)}
                  dataSource={overtimeConfirms}
                  pagination={false}
                  scroll={{ x: 400 }}
                  bordered={false}
                  size='middle'
                />
              </Fragment>
            </Flex>
          </Fragment>
        )}
      </Fragment>
      {openAddNote.id && openAddNote.code && openAddNote.isOpen && (
        <AddNoteForUnConfirm open={openAddNote} setOpen={setOpenAddNote} handleRefresh={handleRefresh} />
      )}
      {openAttach && <OvertimeTicketAttach open={openAttach} setOpen={setOpenAttach} />}
    </Drawer>
  );
}

export default OvertimeTicketDrawer;
